import { z } from 'zod';

const optionalEmail = (message) =>
  z
    .union([
      z.string().trim().length(0).transform(() => ''),
      z.string().trim().email(message),
    ])
    .optional();

const baseSchema = z.object({
  perfil: z.enum(['profissional', 'empreendimento', 'particular']),
  nome: z.string().min(1, 'Nome é obrigatório'),
  sobrenome: z.string().min(1, 'Sobrenome é obrigatório'),
  email: z.string().trim().email('Email inválido'),
  senha: z.string().min(9, 'Senha deve ter no mínimo 9 caracteres'),
  senhaConfirmacao: z.string().min(1, 'Confirme a senha'),
  nif: z.string().optional(),
  isentoIVA: z.enum(['sim', 'nao']).default('nao'),
  isencaoUSUARIO: z.string().trim().optional(),
  enderecoFiscal: z.object({
    rua: z.string().min(1, 'Rua/Número é obrigatório'),
    codigoPostal: z.string().min(1, 'Código postal é obrigatório'),
    freguesia: z.string().min(1, 'Freguesia é obrigatória'),
    pais: z.string().min(1, 'País é obrigatório'),
  }),
  telefone: z.string().min(1, 'Telefone é obrigatório'),
  aceiteTermos: z.boolean().refine(v => v === true, {
    message: 'É necessário aceitar os termos e condições',
  }),
});

const profEmpExtras = z.object({
  designacaoFiscal: z.string().optional(),
  numeroLicenca: z.string().optional(),
  // Confirmação de telefone apenas para profissional/empreendimento
  telefoneConfirmacao: z.string().min(1, 'Confirmação é obrigatória'),
  possuiEnderecoCorrespondencia: z.boolean().default(false),
  // Endereço de Correspondência (opcional, mas obrigatório se o switch estiver ligado)
  enderecoCorrespondencia: z.object({
    rua: z.string().optional(),
    codigoPostal: z.string().optional(),
    freguesia: z.string().optional(),
  }).optional(),
  // Contato para funcionários (opcional via switch)
  contatoFuncionariosDiferente: z.boolean().default(false),
  telefoneFuncionarios: z.string().optional(),
  emailFuncionarios: optionalEmail('E-mail inválido'),
}).superRefine((val, ctx) => {
  if (val.possuiEnderecoCorrespondencia) {
    const ec = val.enderecoCorrespondencia || {};
    if (!ec.rua) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['enderecoCorrespondencia','rua'], message: 'Rua/Número é obrigatório' });
    if (!ec.codigoPostal) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['enderecoCorrespondencia','codigoPostal'], message: 'Código postal é obrigatório' });
    if (!ec.freguesia) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['enderecoCorrespondencia','freguesia'], message: 'Freguesia é obrigatória' });
  }
});

export const formSchema = z.discriminatedUnion('perfil', [
  baseSchema.extend({ perfil: z.literal('profissional') }).merge(profEmpExtras),
  baseSchema.extend({ perfil: z.literal('empreendimento') }).merge(profEmpExtras),
  // 'particular' não exige campos extras de empresa, mas aceita numeroLicenca opcional
  baseSchema.extend({
    perfil: z.literal('particular'),
    numeroLicenca: z.string().optional(),
    emailContato: z.string().trim().email('Email de contacto inválido'),
    // Igualar comportamento de "Contactos para comunicação" aos demais perfis
    contatoFuncionariosDiferente: z.boolean().default(false),
    telefoneFuncionarios: z.string().optional(),
    emailFuncionarios: optionalEmail('E-mail inválido'),
  }),
])
  .superRefine((val, ctx) => {
    if (val.isentoIVA === 'sim' && !val.isencaoUSUARIO?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['isencaoUSUARIO'], message: 'Indique o código de isenção aplicável à compra da assinatura/anúncio.' });
    }
    if (val.senha !== val.senhaConfirmacao) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['senhaConfirmacao'],
        message: 'As senhas não coincidem',
      });
    }
    // Validação condicional: NIF obrigatório apenas para Portugal (country_id 1 no Moloni)
    const paisSelecionado = val.enderecoFiscal?.pais;
    if (paisSelecionado === '1' && !val.nif) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nif'],
        message: 'NIF é obrigatório para Portugal',
      });
    }
    // Designação fiscal obrigatória para profissional/empreendimento em Portugal
    if ((val.perfil === 'profissional' || val.perfil === 'empreendimento') && paisSelecionado === '1' && !val.designacaoFiscal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['designacaoFiscal'],
        message: 'Designação fiscal é obrigatória para Portugal',
      });
    }
  });

